import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../app/AuthProvider";
import { useTheme } from "../app/ThemeProvider";
import { useBranding } from "../api/hooks";
import { resolveAppName } from "../lib/branding";
import { ErrorNotice, Field, Input } from "../components/ui";
import {
  cleanSecondFactorInput,
  lockoutMessage,
  secondFactorFromError,
  type SecondFactorKind,
} from "../lib/second-factor";
import { IconMoon, IconSun, IconSystem } from "../components/icons";

const THEME_ICONS = { LIGHT: IconSun, DARK: IconMoon, SYSTEM: IconSystem } as const;
const THEME_SHORT_LABELS = { LIGHT: "Claro", DARK: "Oscuro", SYSTEM: "Sistema" } as const;

/**
 * Acceso al panel. Si el usuario ha activado un segundo factor (app de
 * autenticación o PIN de conductor), la API responde indicando cuál
 * (`details.secondFactor`); el formulario lo revela en el mismo paso en lugar
 * de abrir una pantalla aparte, para no perder lo ya escrito.
 */
export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { preference, resolved, cycle } = useTheme();
  const branding = useBranding();
  const appName = resolveAppName(branding.data?.appName);

  useEffect(() => {
    document.title = `${appName} · Panel de gestión`;
  }, [appName]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [secondFactorCode, setSecondFactorCode] = useState("");
  // null = todavía no se sabe si hace falta; se descubre con la respuesta de la API.
  const [secondFactor, setSecondFactor] = useState<SecondFactorKind | null>(null);
  const [pinExpired, setPinExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const code = cleanSecondFactorInput(secondFactorCode);
      await auth.login(
        email,
        password,
        secondFactor === "pin" ? { pin: code } : secondFactor === "totp" ? { mfaCode: code } : undefined,
      );
      navigate("/", { replace: true });
    } catch (caught) {
      const request = secondFactorFromError(caught);
      if (request) {
        // La API ya validó el correo y la contraseña: se conservan y solo se pide el código.
        setSecondFactor(request.kind);
        setPinExpired(request.expired);
        // Si el código falló (no es el aviso inicial de "falta el código"), se vacía para reescribirlo.
        if (secondFactorCode) setSecondFactorCode("");
      }
      setError(
        lockoutMessage(caught) ?? (caught instanceof Error ? caught.message : "No se pudo iniciar sesión"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const ThemeIcon = THEME_ICONS[preference];
  const isDark = resolved === "dark";

  // Wordmark: si el super-admin ha subido uno propio lo usamos; si no, el
  // de la plataforma que vive en /public/.
  const wordmarkSrc = isDark
    ? (branding.data?.wordmarkDark ?? "/brand-wordmark-dark.png")
    : (branding.data?.wordmarkLight ?? "/brand-wordmark-light.png");

  // Icono: igual, primero el configurado, luego el de la plataforma.
  const iconSrc = branding.data?.icon ?? "/brand-icon.png";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ink-100 px-4 py-10 dark:bg-ink-950">
      <div className="w-full max-w-[420px]">

        {/* ── Wordmark fuera de la card ── */}
        <div className="mb-8 flex flex-col items-start gap-1 pl-1">
          <img
            src={wordmarkSrc}
            alt={appName}
            className="h-10 w-auto max-w-[200px] object-contain"
          />
          <p className="micro-label text-ink-400 dark:text-ink-500">
            Orden FOM/2861/2012 · art. 6
          </p>
        </div>

        {/* ── Card principal ── */}
        <div className="rounded-2xl border border-ink-200 bg-white shadow-lg dark:border-ink-800 dark:bg-ink-900">

          {/* Cabecera de la card: icono + título */}
          <div className="flex flex-col items-center gap-3 border-b border-ink-100 px-8 py-7 dark:border-ink-800">
            <img
              src={iconSrc}
              alt={appName}
              className="h-16 w-16 rounded-xl object-contain"
            />
            <div className="text-center">
              <h1 className="text-xl font-bold text-ink-900 dark:text-ink-50">
                Panel de gestión
              </h1>
              <p className="mt-0.5 text-sm text-ink-500 dark:text-ink-400">
                Introduce tus credenciales para acceder
              </p>
            </div>
          </div>

          {/* Formulario */}
          <form onSubmit={onSubmit} className="space-y-5 px-8 py-7">

            <Field label="Correo electrónico" htmlFor="email">
              <div className="relative mt-1">
                {/* icono sobre */}
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </span>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="nombre@empresa.es"
                  className="pl-9 py-2.5"
                />
              </div>
            </Field>

            <Field label="Contraseña" htmlFor="password">
              <div className="relative mt-1">
                {/* icono candado */}
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <Input
                  id="password"
                  type={passwordVisible ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-10 py-2.5"
                />
                {/* botón ojo */}
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setPasswordVisible((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-ink-400 hover:text-ink-600 dark:hover:text-ink-300"
                  aria-label={passwordVisible ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {passwordVisible ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.24 4.24M9.36 5.42A11.5 11.5 0 0 1 12 5c7 0 11 7 11 7a17.9 17.9 0 0 1-3.35 4.24M6.6 6.6C3.79 8.32 1 12 1 12s4 7 11 7a10.4 10.4 0 0 0 3.4-.57" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                      <circle cx={12} cy={12} r={3} />
                    </svg>
                  )}
                </button>
              </div>
            </Field>

            {secondFactor === "totp" ? (
              <Field
                label="Código de verificación"
                htmlFor="second-factor"
                hint="Los 6 dígitos de tu aplicación de autenticación. Si perdiste el móvil, escribe un código de recuperación."
              >
                <Input
                  id="second-factor"
                  inputMode="text"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  maxLength={24}
                  value={secondFactorCode}
                  onChange={(event) => setSecondFactorCode(event.target.value)}
                  className="mt-1 py-2.5 tracking-widest"
                />
              </Field>
            ) : null}

            {secondFactor === "pin" ? (
              <Field
                label="PIN de acceso"
                htmlFor="second-factor"
                hint={
                  pinExpired
                    ? "El PIN temporal ha caducado: pide a tu administrador que genere uno nuevo."
                    : "Los 6 dígitos de tu PIN. Si lo olvidaste, tu administrador puede generarte uno nuevo."
                }
              >
                <Input
                  id="second-factor"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{6}"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  maxLength={6}
                  value={secondFactorCode}
                  onChange={(event) => setSecondFactorCode(event.target.value.replace(/\D/g, ""))}
                  className="mt-1 py-2.5 tracking-widest"
                />
              </Field>
            ) : null}

            {error ? <ErrorNotice error={new Error(error)} /> : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-md shadow-brand-600/30 transition-all hover:bg-brand-700 hover:shadow-brand-700/30 disabled:cursor-not-allowed disabled:opacity-60 dark:shadow-brand-900/40"
            >
              {submitting ? "Comprobando…" : "Entrar"}
            </button>
          </form>
        </div>

        {/* ── Selector de tema ── */}
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={cycle}
            aria-label={`Tema: ${THEME_SHORT_LABELS[preference]}. Pulsa para cambiar`}
            className="inline-flex items-center gap-2 rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-500 transition-colors hover:bg-ink-50 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-400 dark:hover:bg-ink-800"
          >
            <ThemeIcon className="h-4 w-4" />
            Tema: {THEME_SHORT_LABELS[preference]}
          </button>
        </div>

      </div>
    </div>
  );
}
